// HerKartStores.jsx
// PART 1

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faSearch,
  faFilter,
  faStore,
  faSortAmountDown,
  faFlask,
  faShoppingBag,
  faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";

import StoreCard from "./components/StoreCard";
import storesData from "./fallback/stores";
import { useHerKart } from "./hooks/useHerKart";

const HerKartStores = () => {
  const navigate = useNavigate();

  const { storeCode: urlStoreCode } = useParams();

  const { fetchLiveProducts, fetchStoreByCode } = useHerKart();

  const [loadingStores, setLoadingStores] = useState(true);
  const [liveProducts, setLiveProducts] = useState([]);
  const [showSamples, setShowSamples] = useState(false);

  const sampleStores = useMemo(
    () =>
      storesData.filter(Boolean).map((item) => ({
        ...item,
        isSample: true,
      })),
    [],
  );

  const products = useMemo(() => {
    return showSamples ? [...liveProducts, ...sampleStores] : liveProducts;
  }, [liveProducts, sampleStores, showSamples]);

  useEffect(() => {
    const loadProducts = async () => {
      setLoadingStores(true);

      try {
        const response = await fetchLiveProducts();

        if (response?.success) {
          setLiveProducts(
            (response.products || []).map((item) => ({
              ...item,
              isSample: false,
            })),
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingStores(false);
      }
    };

    loadProducts();

    // eslint-disable-next-line
  }, []);

  const [storeInfo, setStoreInfo] = useState(null);
  const [storeCheckLoading, setStoreCheckLoading] = useState(!!urlStoreCode);
  const [storeNotFound, setStoreNotFound] = useState(false);

  useEffect(() => {
    if (!urlStoreCode) return;

    const lookup = async () => {
      setStoreCheckLoading(true);
      setStoreNotFound(false);

      try {
        const response = await fetchStoreByCode(urlStoreCode);

        if (response?.success && response.store) {
          setStoreInfo(response.store);
        } else {
          setStoreNotFound(true);
        }
      } catch (e) {
        console.error(e);
        setStoreNotFound(true);
      } finally {
        setStoreCheckLoading(false);
      }
    };

    lookup();

    // eslint-disable-next-line
  }, [urlStoreCode]);

  const [search, setSearch] = useState("");

  const [selectedCategory, setSelectedCategory] = useState("All");

  const [sortBy, setSortBy] = useState("featured");

  const [showPrivateStoreFilter, setShowPrivateStoreFilter] = useState(false);

  const [manualStoreCodeInput, setManualStoreCodeInput] = useState("");

  const [manualStoreCode, setManualStoreCode] = useState("");

  const effectiveStoreCode =
    urlStoreCode || (showPrivateStoreFilter ? manualStoreCode : "");

  /*
   * ======================================================
   * BUILD STORE CATALOGUE
   * ======================================================
   */

  const stores = useMemo(() => {
    const map = new Map();

    products.forEach((product) => {
      const key = product.storeCode || product.store || product._id;

      if (!map.has(key)) {
        map.set(key, {
          _id: key,

          storeCode: product.storeCode || "",

          store: product.store || "",

          entrepreneur: product.entrepreneur || "",

          city: product.city || "",

          locality: product.locality || "",

          category: product.category || "",

          banner: product.storeBanner || product.images?.[0] || "",

          logo: product.storeLogo || product.images?.[0] || "",

          rating: product.rating || 0,

          totalRatings: product.totalRatings || 0,

          featured: product.featured,

          bestseller: product.bestseller,

          isInnovation: product.isInnovation,

          isBrand: product.isBrand,

          isPilotSample: product.isPilotSample,

          isSample: product.isSample,

          productCount: 0,

          categories: new Set(),
        });
      }

      const store = map.get(key);

      store.productCount++;

      if (product.category) {
        store.categories.add(product.category);
      }

      if (product.rating > store.rating) {
        store.rating = product.rating;
      }

      if (product.totalRatings > store.totalRatings) {
        store.totalRatings = product.totalRatings;
      }
    });

    return [...map.values()].map((item) => ({
      ...item,
      categories: [...item.categories],
    }));
  }, [products]);

  const categories = useMemo(() => {
    const unique = [
      ...new Set(stores.flatMap((store) => store.categories).filter(Boolean)),
    ];

    return ["All", ...unique];
  }, [stores]);

  const filteredStores = useMemo(() => {
    let list = [...stores];

    if (effectiveStoreCode) {
      list = list.filter((store) => store.storeCode === effectiveStoreCode);
    }

    if (selectedCategory !== "All") {
      list = list.filter((store) =>
        store.categories.includes(selectedCategory),
      );
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();

      list = list.filter((store) =>
        [
          store.store,
          store.entrepreneur,
          store.city,
          store.locality,
          store.storeCode,
          ...store.categories,
        ]
          .join(" ")
          .toLowerCase()
          .includes(keyword),
      );
    }

    if (effectiveStoreCode) {
      const rank = (store) =>
        store.isInnovation
          ? 0
          : store.isBrand
            ? 1
            : store.isPilotSample
              ? 2
              : 3;

      list.sort((a, b) => rank(a) - rank(b));
    } else {
      switch (sortBy) {
        case "name":
          list.sort((a, b) => a.store.localeCompare(b.store));
          break;

        case "city":
          list.sort((a, b) => (a.city || "").localeCompare(b.city || ""));
          break;

        case "products":
          list.sort((a, b) => b.productCount - a.productCount);
          break;

        default:
          break;
      }
    }

    return list;
  }, [stores, search, selectedCategory, sortBy, effectiveStoreCode]);

  const handleViewStore = (store) => {
    if (store.storeCode) {
      navigate(`/herkart/products/${store.storeCode}`);
      return;
    }

    navigate("/herkart/products");
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col px-4 py-2">
          <div className="flex items-center gap-3">
            <Link
              to="/foundher"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 transition hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div>
              <h1 className="text-xl font-bold">Her Stores</h1>

              <p className="text-xs text-slate-400">
                Discover women-led stores
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <span
              className={`rounded-xl px-2 py-2 text-xs font-semibold ${
                showSamples
                  ? "bg-amber-500/20 text-amber-300"
                  : "bg-emerald-500/20 text-emerald-300"
              }`}
            >
              {showSamples ? "Dummy Stores" : "Live Stores"}
            </span>

            <button
              onClick={() => setShowSamples((prev) => !prev)}
              className={`flex w-fit items-center gap-2 rounded-xl px-2 py-1 text-xs ${
                showSamples
                  ? "bg-white/10 hover:bg-white/20"
                  : "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
              }`}
            >
              <FontAwesomeIcon icon={faFlask} />
              {showSamples ? "Show Live" : "Show Dummy"} {filteredStores.length}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2">
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search stores, entrepreneurs, categories, cities..."
            className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-pink-500 focus:outline-none"
          />
        </div>

        <div className="mt-2 flex flex-row justify-end gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <FontAwesomeIcon
              icon={faSortAmountDown}
              className="text-pink-400"
            />

            <span>Sort By</span>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            disabled={!!effectiveStoreCode}
            className="w-fit rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 text-sm text-white focus:border-pink-500 focus:outline-none disabled:opacity-50"
          >
            <option value="name">Name (A-Z)</option>
            <option value="city">City</option>
          </select>
        </div>
      </div>

      {/* <div className="mt-5 grid grid-cols-3 gap-3"> */}
        {urlStoreCode && storeInfo && (
          <div className="mt-6 rounded-2xl border border-pink-500/30 bg-pink-500/10 p-4">
            <p className="text-sm text-pink-200">
              Viewing private store :
              <span className="ml-2 font-semibold">{storeInfo.storeName}</span>{" "}
              ({storeInfo.storeCode})
            </p>

            {storeInfo.storeHeadline && (
              <p className="mt-1 text-xs text-pink-300">
                {storeInfo.storeHeadline}
              </p>
            )}
          </div>
        )}
        {urlStoreCode && storeCheckLoading ? (
          <div className="mt-16 text-center text-slate-400">
            Checking store...
          </div>
        ) : urlStoreCode && storeNotFound ? (
          <div className="mt-16 rounded-3xl border border-dashed border-red-500/30 bg-red-500/5 p-10 text-center">
            <FontAwesomeIcon
              icon={faStore}
              className="mx-auto mb-4 text-4xl text-red-400"
            />

            <h3 className="text-xl font-semibold">Store not found</h3>

            <p className="mt-2 text-sm text-slate-400">
              No active store exists with the code "{urlStoreCode}
              ".
            </p>

            <Link
              to="/herkart/stores"
              className="mt-5 inline-block rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold hover:bg-pink-500"
            >
              Browse All Stores
            </Link>
          </div>
        ) : loadingStores ? (
          <div className="mt-16 text-center text-slate-400">
            Loading stores...
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {filteredStores.map((store) => (
              <StoreCard
                key={store._id}
                store={store}
                onView={handleViewStore}
              />
            ))}
          </div>
        )}
      {/* </div> */}

      {!loadingStores && !storeNotFound && filteredStores.length === 0 && (
        <div className="mt-16 rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
          <FontAwesomeIcon
            icon={faStore}
            className="mx-auto mb-4 text-4xl text-slate-500"
          />

          <h3 className="text-sm font-semibold text-yellow-100 lg:text-xl">
            No Stores Found
          </h3>

          <p className="mt-2 text-sm text-slate-400">
            Try changing your search, category or store code.
          </p>
        </div>
      )}
    </div>
  );
};

export default HerKartStores;
